import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

function Privacy() {
  const currentYear = new Date().getFullYear();
  const contact = siteConfig.contact || {};

  return (
    <main className="content-page legal-page privacy-page">
      <header className="page-hero page-hero--compact">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Your information</p>
          <h1>Privacy policy</h1>
          <p className="page-hero__lede">Last updated: {currentYear}</p>
          <p className="page-hero__lede">
            Demo policy template — review with qualified counsel and your real
            service providers before a production launch.
          </p>
        </div>
      </header>

      <section className="page-section page-shell legal-layout">
        <nav className="policy-nav" aria-label="Privacy policy sections">
          <p className="eyebrow">Contents</p>
          <a href="#privacy-overview">Overview</a>
          <a href="#privacy-collection">Information we collect</a>
          <a href="#privacy-use">How we use information</a>
          <a href="#privacy-sharing">How we disclose information</a>
          <a href="#privacy-choices">Your choices and rights</a>
          <a href="#privacy-contact">Contact us</a>
        </nav>

        <article className="prose legal-copy">
          <section id="privacy-overview">
            <h2>1. Overview</h2>
            <p>
              This front-end demo stores cart and wishlist selections in your
              browser. Account, checkout, contact, wholesale, and gift forms
              collect only the information needed for their stated purpose. The
              sections below are a production-readiness template for how a
              connected {siteConfig.brandName} store may handle information.
            </p>
          </section>

          <section id="privacy-collection">
            <h2>2. Information we collect</h2>
            <p>A connected production store may collect:</p>
            <ul>
              <li>
                <strong>Contact and account details,</strong> such as your name,
                email, billing and delivery addresses, and
                account credentials.
              </li>
              <li>
                <strong>Order and payment details,</strong> including products
                purchased, transaction history, gift messages, and limited
                payment information handled by our payment providers.
              </li>
              <li>
                <strong>Communications,</strong> including customer care
                requests, reviews, survey responses, and marketing preferences.
              </li>
              <li>
                <strong>Device and activity data,</strong> such as IP address,
                browser type, pages viewed, referring links, and cookie
                identifiers.
              </li>
            </ul>
            <p>
              We receive information directly from you, automatically from your
              device, and from providers that support payments, delivery,
              analytics, fraud prevention, and advertising.
            </p>
          </section>

          <section id="privacy-use">
            <h2>3. How we use information</h2>
            <p>A production store may use personal information to:</p>
            <ul>
              <li>process payment, fulfill orders, and manage returns;</li>
              <li>provide customer care and communicate service updates;</li>
              <li>maintain, personalize, secure, and improve our services;</li>
              <li>detect fraud, misuse, and technical or security issues;</li>
              <li>comply with law and enforce our agreements.</li>
            </ul>
          </section>

          <section id="privacy-sharing">
            <h2>4. How we disclose information</h2>
            <p>
              We disclose information only as reasonably needed to service
              providers that help us operate—for example payment processors,
              ecommerce hosting, delivery carriers, analytics, communications,
              customer support, and fraud prevention providers. We may also
              disclose information to comply with law, protect rights and
              safety, or complete a financing, merger, sale, or other business
              transaction.
            </p>
            <p>
              We do not sell personal information for money. Some cookie-enabled
              analytics or advertising activity may be treated as a “sale,”
              “sharing,” or targeted advertising under certain privacy laws;
              where applicable, you may opt out as described below.
            </p>
          </section>

          <section>
            <h2>5. Cookies and similar technologies</h2>
            <p>
              Cookies help keep the cart working, remember preferences,
              understand site performance, and support relevant communications.
              You can limit cookies using browser settings. Blocking essential
              cookies may prevent checkout or other site features from working
              correctly.
            </p>
          </section>

          <section>
            <h2>6. Retention and security</h2>
            <p>
              We retain information for as long as necessary for the purposes
              above, including legal, accounting, dispute, and fraud-prevention
              needs. We use reasonable administrative, technical, and physical
              safeguards, but no online system or transmission is completely
              secure.
            </p>
          </section>

          <section id="privacy-choices">
            <h2>7. Your choices and privacy rights</h2>
            <p>
              Essential order and service messages are used only to support
              your purchase or request. Depending on where you live, you may also have rights to access,
              correct, delete, or receive a copy of personal information, and to
              opt out of or limit certain processing. We may verify your
              identity before completing a request. Authorized agents may submit
              requests where local law allows. We will not discriminate against
              you for exercising a privacy right.
            </p>
          </section>

          <section>
            <h2>8. Children and international visitors</h2>
            <p>
              Our services are not directed to children under 13, and we do not
              knowingly collect their personal information. If you access the
              site from outside the country where our business operates, your
              information may be processed in that country under its laws.
            </p>
          </section>

          <section>
            <h2>9. Changes to this policy</h2>
            <p>
              We may update this policy as our practices or legal obligations
              change. The revised version will appear here with a new “last
              updated” date.
            </p>
          </section>

          <section id="privacy-contact">
            <h2>10. Contact us</h2>
            <p>
              Questions or privacy requests can be sent through our{" "}
              <Link to="/contact">contact page</Link>
              {contact.email && (
                <>
                  {" "}
                  or by email at{" "}
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </>
              )}
              .
            </p>
            {contact.address && <address>{contact.address}</address>}
          </section>
        </article>
      </section>
    </main>
  );
}

export default Privacy;
